import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ENV } from '@/constants/envConfig';
//import { utcToZonedTime } from 'date-fns-tz';
import { useDispatch, useSelector } from '@/redux';
import { useBasketMutating } from '@/hooks/useBasketMutating';
import orderStatusActions from '@/redux/orderStatus/actions';
import { OrderStatuses, OrderStatusTypes } from '@/types/orderStatus';
//import { getStoreTimeInfo } from '@/utils/locations';
//import { useGlobalDispatch } from '@/redux/global/reducer';
//import basketActions from '@/redux/basket/actions';

//const MAX_VALIDATIONS = 8;

const OrderStatus = () => {
  //const clearValidationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  //const [validationCounter, setValidationCounter] = useState(0);
  //const [lastValidationTime, setLastValidationTime] = useState<Date | null>(null);
  const dispatch = useDispatch();
  //const { displayToast } = useGlobalDispatch();
  const { location, checkoutBasket } = useSelector((state) => state.app.basket);
  const { orderStatus } = useSelector((state) => state.app);
  const basketMutating = useBasketMutating();
  //const { storeTimezone } = getStoreTimeInfo(location);
  const hasValidatedRef = useRef(false);
  // Any validate already in flight satisfies the mount validate. INITIALIZING_ORDER runs one as its
  // last step; VALIDATING_ORDER covers every other caller — notably the 'validate' recovery
  // modal's Try Again (forceOrderPending + validateOrder), which runs while this component is
  // deferring for the modal (TEAM-15341). Without this latch that Try Again sent /validate twice.
  if (
    orderStatus.status === OrderStatuses.INITIALIZING_ORDER ||
    orderStatus.status === OrderStatuses.VALIDATING_ORDER
  ) {
    hasValidatedRef.current = true;
  }

  // Validate once on component mount only.
  //
  // `basketMutating` replaces what used to be an explicit list of statuses to sit
  // out (INITIALIZING_ORDER, VALIDATING_ORDER, WANTED_AT_*, CONVEYANCE_*). That
  // list drifted from the set of statuses that actually mean "a write is in
  // flight" — TIP_ADDING, PROMO_CODE_ADDING, STORED_VALUE_POSTING,
  // PAYMENT_APPLYING and BASKET_REWARDS_GETTING were all missing — so mounting
  // during one of those dispatched a validate that basketMutationGuard held back
  // while `hasValidatedRef` latched anyway, and the mount validation never
  // happened (TEAM-14696). The effect re-runs when `orderStatus.status` changes,
  // so the validate fires as soon as the in-flight write settles.
  //
  // It also sits out an open recovery modal (TEAM-15341). A setConveyance that 502s twice at
  // checkout start raises the conveyance recovery modal while the diner is still on the store
  // page; arriving here, this validate used to run straight away and its VALIDATE_ORDER ->
  // ORDER_PENDING/OK overwrote RECOVERY_502, so the modal vanished before the diner could read
  // it. RECOVERY_502 is not a basket write, so `basketMutating` does not cover it. The same
  // check stops this validate clobbering every other recovery endpoint (validate, rewards,
  // wanted_time, submit). The ref only latches when the validate is actually dispatched, so
  // once the modal is dismissed (Try Again -> forceOrderPending) the deferred validate runs
  // and the basket gets priced.
  const recoveryModalOpen = orderStatus.type === OrderStatusTypes.RECOVERY_502;
  useEffect(() => {
    if (
      !hasValidatedRef.current &&
      !basketMutating &&
      !recoveryModalOpen &&
      checkoutBasket?.id &&
      location.id
    ) {
      dispatch(orderStatusActions.validateOrder(checkoutBasket?.id, location.id));
      hasValidatedRef.current = true;
    }
  }, [checkoutBasket?.id, location.id, basketMutating, recoveryModalOpen, dispatch]);

  // Periodically validate order every 15 minutes increment of the store's local time
  /*  useEffect(() => {
    if (checkoutBasket?.id == null || location.id == null) return;
    clearValidationIntervalRef.current = setInterval(() => {
      const now = utcToZonedTime(new Date(), storeTimezone);
      const is15MinIncrement = now.getMinutes() % 15 === 0 && now.getSeconds() === 0;
      if (is15MinIncrement) {
        dispatch(orderStatusActions.validateOrder(checkoutBasket?.id, location.id));
        setValidationCounter((prev) => prev + 1);
      }
    }, 1000);
    return () =>
      clearValidationIntervalRef.current
        ? clearInterval(clearValidationIntervalRef.current)
        : undefined;
  }, [dispatch, checkoutBasket?.id, location.id, storeTimezone]); */

  /* useEffect(() => {
    if (clearValidationIntervalRef.current && validationCounter === MAX_VALIDATIONS) {
      clearInterval(clearValidationIntervalRef.current);
      dispatch(displayToast('Your previous order has expired.'));
      dispatch(basketActions.destroyBasket());
    }
  }, [validationCounter, dispatch, displayToast]); */

  return (
    <StyledOrderStatusContainer $isVisible={['dev', 'staging'].includes(ENV)}>
      <StyledOrderStatusContent>
        <StyledEnvironmentLabel>{ENV.toLocaleUpperCase()} Environment</StyledEnvironmentLabel>
        <StyledStatusLabel>
          Type: <StyledStatusValue>{orderStatus.type}</StyledStatusValue>
        </StyledStatusLabel>
        <StyledStatusLabel>
          Status: <StyledStatusValue>{orderStatus.status}</StyledStatusValue>
        </StyledStatusLabel>
        <StyledStatusLabel>
          Message: <StyledStatusValue>{orderStatus.message ?? 'null'}</StyledStatusValue>
        </StyledStatusLabel>
      </StyledOrderStatusContent>
      <StyledDisclaimer>Only visible in dev and staging environments</StyledDisclaimer>
    </StyledOrderStatusContainer>
  );
};

export default OrderStatus;

// Styled Components
const StyledOrderStatusContainer = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  padding: 16px;
  background-color: #f0f0f0;
  color: #333;
`;

const StyledOrderStatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0px 24px;
`;

const StyledEnvironmentLabel = styled.p`
  font-weight: 800;
  font-size: 20px;
  margin: 0px;
`;

const StyledStatusLabel = styled.p`
  margin: 0;
`;

const StyledStatusValue = styled.span`
  font-weight: 800;
  font-size: 20px;
  margin: 0px;
`;

const StyledDisclaimer = styled.p`
  margin: 0px;
`;
